// VERA-825 / VERA-246: Payment-method picker for the tag shop checkout.
// A selectable list of the company's saved cards. Confirming updates the chosen
// card (used to confirm the PaymentIntent client-side). "Add a new card" routes
// to the Stripe Elements add-card path handled by the page.

import React, {useEffect, useState} from 'react';

import {Stack, Box, useTheme} from '@mui/material';
import {Button, Modal} from '@repo/ui';
import {CheckIcon, PlusIcon} from 'lucide-react';

import type {PaymentMethodRow} from 'hooks/api/usePaymentMethods';
import {PaymentMethodPreview} from 'components/display/PaymentMethodPreview';

interface ChangePaymentModalProps {
  open: boolean;
  onClose(): void;
  methods: PaymentMethodRow[];
  selectedId: string | null;
  onSelect(method: PaymentMethodRow): void;
  onAddNew(): void;
}

export const ChangePaymentModal = ({
  open,
  onClose,
  methods,
  selectedId,
  onSelect,
  onAddNew,
}: ChangePaymentModalProps) => {
  const theme = useTheme();
  const [pendingId, setPendingId] = useState<string | null>(selectedId);

  // Re-preselect the current card every time the modal opens.
  useEffect(() => {
    if (open) setPendingId(selectedId);
  }, [open, selectedId]);

  const handleConfirm = () => {
    const chosen = methods.find((m) => m.id === pendingId);
    if (chosen) onSelect(chosen);
    onClose();
  };

  return (
    <Modal
      title="Change payment method"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!pendingId}
          >
            Use this card
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        {methods.map((method) => {
          const isSelected = method.id === pendingId;
          return (
            <Box
              key={method.id}
              role="button"
              tabIndex={0}
              onClick={() => setPendingId(method.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPendingId(method.id);
                }
              }}
              sx={{
                cursor: 'pointer',
                p: 3,
                borderRadius: 2,
                border: `1px solid ${
                  isSelected
                    ? theme.palette.brand[600]
                    : theme.palette.primary[100]
                }`,
                backgroundColor: isSelected
                  ? theme.palette.brand['600-6%']
                  : 'transparent',
                transition: 'all 0.15s ease-in',
                '&:hover': {
                  backgroundColor: theme.palette.brand['600-6%'],
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={3}
              >
                <PaymentMethodPreview method={method} />
                {isSelected && (
                  <CheckIcon
                    size={20}
                    strokeWidth={2}
                    color={theme.palette.common.white}
                  />
                )}
              </Stack>
            </Box>
          );
        })}

        <Button
          variant="text"
          size="large"
          startIcon={<PlusIcon size={18} strokeWidth={1} />}
          onClick={() => {
            onAddNew();
            onClose();
          }}
          sx={{alignSelf: 'flex-start'}}
        >
          Add a new payment method
        </Button>
      </Stack>
    </Modal>
  );
};
